import styled from 'styled-components';

import { FieldNames, SettingsReadOnlyText } from 'modules/templateEditor/common';
import { InAppContentDefinition } from 'modules/graphql/types';
import { NegativeFill, OpenNewWindow16 } from 'ui/icons';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { colors, spacing, textColors } from 'ui/theme';

import { LinkContent, ReadOnlyLink } from 'modules/templateEditor/common/styles';
import { useErrorValidationContext } from 'modules/templateEditor/context/ErrorValidationContext';
import { constructFieldErrors, getIsEditMode, validateUrl } from '../../../common/utils';
import { useQueryParamContext } from '../../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../../constants';

const ReadOnlyWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: ${spacing.half};
`;

const StyledReadOnlyLink = styled(ReadOnlyLink)`
  padding-bottom: 0;
`;

export const RokuImageUrl = () => {
  const { view } = useQueryParamContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { ottPayload } = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  const { hasError: hasRokuImageUrlError } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.ROKU_FHD_IMAGE,
  });

  const ReadOnly = ottPayload?.fhdImage ? (
    <ReadOnlyWrapper>
      {hasRokuImageUrlError && <NegativeFill color={colors.red100} height={16} width={16} />}
      <StyledReadOnlyLink
        href={validateUrl(ottPayload.fhdImage)}
        target="_blank"
        rel="noreferrer noopener"
      >
        <LinkContent data-test="fhd-image-url" isUnderlined={true}>
          {ottPayload?.fhdImage}
        </LinkContent>
        <OpenNewWindow16 color={textColors.textLink} />
      </StyledReadOnlyLink>
    </ReadOnlyWrapper>
  ) : (
    <SettingsReadOnlyText isUnset={true} data-test="fhd-image-url">
      {SETTINGS_DISPLAY_CONTENT.roku.imageUrl.unset}
    </SettingsReadOnlyText>
  );

  return (
    <FormField
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.roku.imageUrl.label}
      hasError={ottPayload?.fhdImage ? hasRokuImageUrlError : undefined}
      errorText={isEditModeVisible && SETTINGS_DISPLAY_CONTENT.roku.imageUrl.error}
      helpText={isEditModeVisible && SETTINGS_DISPLAY_CONTENT.roku.imageUrl.helpText}
      data-test="form-field-roku-image-url"
    >
      {isEditModeVisible ? (
        <InputText
          id="fhd-image-url-text-editor"
          data-test="fhd-image-url-text-editor"
          onChange={(url) => setCurrentMediumField('ottPayload', { ...ottPayload, fhdImage: url })}
          value={ottPayload?.fhdImage || ''}
          placeholder={SETTINGS_DISPLAY_CONTENT.roku.imageUrl.placeholder}
          width="100%"
        />
      ) : (
        ReadOnly
      )}
    </FormField>
  );
};
